/**
 * All widget CSS, imported as a string (`?inline`) and injected into the
 * Shadow DOM (see mount.tsx).
 *
 * Because it lives inside a shadow root:
 *   - none of these rules leak out to the host page;
 *   - host-page styles cannot reach in (the only inherited properties are
 *     reset on `:host`), so the widget looks identical on any site.
 *
 * Design tokens follow spec §6.1 (Impact Calculator). Fonts degrade to a system
 * stack; the brand fonts are loaded best-effort via fonts.ts.
 */
:host {
  /* Reset inheritable properties so the host page can't bleed in. */
  all: initial;
  --color-bg: #070b1e;
  --color-bg-light: #262e3f;
  --border-color: rgba(255, 255, 255, 0.5);
  --color-text: #fff;
  --color-muted: #cfd6dd;
  --color-lickd-pink: #fd57c9;
  --color-lickd-pink-transparent: rgba(253, 87, 201, 0.4);
  --color-lickd-pink-light: #ffebf8;
  --color-lickd-green: #03dac5;
  --font: 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  --display: 'Bebas Neue', var(--font);
  --radius: 0.5rem;
  --radius-xs: 0.125rem;
  --radius-sm: 0.25rem;
  --radius-lg: 1rem;
  --chart-gutter: 1.5rem;
  --spacing-xxs: 0.25rem;
  --spacing-xs: 0.5rem;
  --spacing-sm: 0.75rem;
  --spacing-default: 1rem;
  --spacing-lg: 1.5rem;
  --spacing-xl: 2rem;
  --spacing-2xl: 3rem;
  --spacing-3xl: 4rem;
  --transition-long: 600ms;
  --transition-medium: 300ms;
  --transition-short: 150ms;
  display: block;
  font-family: var(--font);
  font-size: 1rem;
  font-weight: 400;
  color: var(--color-text);
  line-height: 1.4;
  -webkit-font-smoothing: antialiased;
  text-align: left;
}

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
  letter-spacing: normal;
  word-spacing: normal;
}

.root {
  position: relative;
  background: var(--color-bg);
  overflow: hidden;
  max-width: 860px;
  margin: 0 auto;
}

.root :where(h1, h2, h3, h4) {
  font-family: var(--display);
  font-weight: 400;
  line-height: 1.1;
  margin: 0 0 var(--spacing-default);
}

.root :where(h1) {
  font-size: 3rem;
}
.root :where(h2) {
  font-size: 2.5rem;
}
.root :where(h3) {
  font-size: 2rem;
}
.root :where(h4) {
  font-size: 1.75rem;
}

@keyframes chart-bar-in {
  from {
    transform: scaleY(0);
  }
  to {
    transform: scaleY(1);
  }
}
@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes pulse-glow {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.text-center {
  text-align: center;
}

.inner {
  position: relative;
  z-index: 1;
}

.search {
  max-width: 560px;
  margin: 0 auto;
}
.search-inner {
  display: flex;
  gap: var(--spacing-sm);
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  padding: 0.5rem 0.5rem 0.5rem 1rem;
  transition: border-color 0.2s;
}
.search-inner:focus-within {
  border-color: var(--color-lickd-pink);
}
.search-input {
  flex: 1;
  background: none;
  border: none;
  outline: none;
  font: inherit;
  color: var(--color-text);
  min-width: 0;
}
.search-input::placeholder {
  color: var(--color-muted);
}

.form-sub {
  margin-top: var(--spacing-sm);
  font-size: 0.75rem;
  color: var(--color-muted);
}

.btn {
  border: 1px solid;
  border-radius: var(--radius-sm);
  padding: var(--spacing-xs) var(--spacing-default);
  font-family: var(--font);
  font-size: 1rem;
  font-weight: 700;
  text-align: center;
  cursor: pointer;
  text-decoration: none;
  transition:
    border-color 0.15s,
    background-color 0.15s,
    color 0.15s;
  white-space: nowrap;
}

.btn-primary {
  background-color: var(--color-lickd-pink);
  border-color: var(--color-lickd-pink);
  color: #070b1e;
}
.btn-primary:hover {
  background-color: #fd21b7;
  border-color: #fd21b7;
}

.btn-secondary {
  background-color: var(--color-bg);
  color: var(--color-text);
  border-color: var(--color-text);
}
.btn-secondary:hover {
  color: var(--color-lickd-pink);
  border-color: var(--color-lickd-pink);
}

.btn:disabled {
  border-color: #a0aebb;
  background-color: var(--color-bg);
  color: #a0aebb;
  cursor: not-allowed;
  transform: none;
}

/* Loading */
.loading {
  text-align: center;
  padding: 60px 24px;
}
.spinner {
  width: 36px;
  height: 36px;
  border: 3px solid var(--color-muted);
  border-top-color: var(--color-lickd-pink);
  border-radius: 50%;
  animation: lic-spin 0.8s linear infinite;
  margin: 0 auto var(--spacing-default);
}
@keyframes lic-spin {
  to {
    transform: rotate(360deg);
  }
}
.loading-txt {
  font-size: 1rem;
  color: var(--color-muted);
  animation: fade-in var(--transition-medium) ease-out;
}

/* Error */
.err {
  text-align: center;
  padding: 48px 24px;
}
.err-title {
  font-size: 1.25rem;
  font-weight: 700;
}
.err-msg {
  color: var(--color-muted);
  margin: 1rem 0;
}

/* Results */
.results {
}
.back {
  background: none;
  border: none;
  color: var(--color-text);
  font-family: var(--font);
  font-size: 0.75rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 0;
  margin-bottom: 16px;
  transition: color 0.15s;
}
.back:hover {
  color: var(--color-lickd-green);
}

.channel-card {
  background: var(--color-bg-light);
  border-radius: var(--radius);
  padding: var(--spacing-default);
  display: flex;
  align-items: center;
  gap: var(--spacing-default);
}
.channel-image {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  object-fit: cover;
  background: var(--color-bg);
  flex-shrink: 0;
}
.channel-info {
  flex: 1;
  min-width: 0;
}
.channel-name {
  margin: 0;
}
.channel-meta {
  font-size: 0.75rem;
  color: var(--color-muted);
  margin-top: 3px;
}
.channel-badges {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
  flex-wrap: wrap;
}
.badge {
  font-size: 0.75rem;
  padding: 2px 8px;
  border-radius: var(--radius-sm);
  color: var(--color-muted);
  border: 1px solid var(--border-color);
}
.badge-green {
  color: var(--color-lickd-green);
  border-color: var(--color-lickd-green);
}

.badge-pink {
  color: var(--color-lickd-pink);
  border-color: var(--color-lickd-pink);
}

.results-container {
  margin-top: var(--spacing-xl);
  animation: fade-in var(--transition-long) ease-out forwards;
}

.headline {
  margin: var(--spacing-2xl) 0;
}
.headline .hn {
  font-family: var(--display);
  font-size: 3rem;
  line-height: 1;
}

.headline .uplift-pct {
  display: block;
  font-size: 4.5rem;
  color: var(--color-lickd-pink);
}

.headline .hs {
  font-size: 0.75rem;
  color: var(--color-muted);
  margin-top: 6px;
}
.headline.insufficient .hn {
  font-size: 2.25rem;
  line-height: 1.1;
  color: var(--color-text);
  text-wrap: balance;
  max-width: 750px;
  margin: 0 auto;
}

.narrative-cards {
  display: grid;
  gap: var(--spacing-default);
  grid-template-columns: 1fr;
  list-style: none;
  padding-left: 0;
  margin-left: 0;
}

.narr-card {
  display: block;
  text-align: center;
  border-radius: var(--radius);
  border: 1px solid var(--border-color);
  padding: var(--spacing-default);
}

.narr-card-inner {
  display: block;
  text-wrap: balance;
}

.narr-card strong {
  color: var(--color-lickd-pink);
  font-weight: 700;
}

.narr-card--centre {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.narr-card-headline {
  display: block;
  font-size: 2.75rem;
  font-family: var(--display);
  font-weight: 400;
  line-height: 1.1;
  color: var(--color-lickd-green);
  margin: 0 0 var(--spacing-xxs);
}

.footnotes {
  margin: var(--spacing-2xl) 0;
}

.footnotes-list {
  padding-left: var(--spacing-lg);
  margin: 0;
}

.footnotes-list > li strong {
  color: var(--color-lickd-pink);
  font-weight: 700;
}

.footnotes-list > li::marker {
  color: var(--color-lickd-green);
  font-size: 1.5em;
}

.footnotes > li + li {
  margin-top: var(--spacing-xs);
}

.stats {
  margin-top: var(--spacing-2xl);
}

.projections {
  width: 100%;
  overflow: auto;
  margin-bottom: var(--spacing-2xl);
}

.projections-table {
  text-align: left;
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
  color: var(--color-muted);
}

.projections-table :where(td, th) {
  padding: var(--spacing-xs) var(--spacing-sm);
  border: 2px solid var(--color-bg);
}

.projections-table tbody :where(td, th) {
  background-color: var(--color-bg-light);
}

.projections-table :where(td, th):is(:not(:first-child)) {
  text-align: center;
}

.projections-actual {
  color: var(--color-muted);
}

.projections-lickd {
  font-weight: 700;
  color: var(--color-lickd-pink);
}

.chart-card {
  background: var(--color-bg-light);
  padding: var(--spacing-lg) var(--spacing-default);
  border-radius: var(--radius);
}
/* Available to screen readers, removed from the visual layout. Not
   `display: none`, which would hide it from assistive tech too. */
.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

/* Positioning context for the Y axis labels, so they pin to the top and bottom
   of the plot (whose height is set on .chart-grid below) at every width. */
.chart-plot {
  position: relative;
  padding-left: var(--chart-gutter);
  margin: var(--spacing-default) 0;
}

.chart-grid-wrapper {
  border-color: var(--color-muted);
  border-bottom: 1px solid;
  border-left: 1px solid;
}

/* The plot area. Its height is fixed in CSS pixels rather than coming from an
   aspect ratio, so bar heights stay honest percentages of the axis maximum and
   the labels have something definite to pin to. */
.chart-grid {
  display: flex;
  align-items: flex-end;
  justify-content: space-evenly;
  gap: 2px;
  height: 200px;
}

/* One video: the actual and with-Lickd bars sit side by side, sharing the slot
   and growing from the baseline. */
.chart-slot {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 2px;
  flex: 1 1 0;
  height: 100%;
  min-width: 0;
}

.chart-bar {
  flex: 1 1 0;
  max-width: 26px;
  min-height: 1px;
  border-radius: var(--radius-xs) var(--radius-xs) 0 0;
  transform-origin: bottom;
  transform: scaleY(0);
}

.chart-bar-actual {
  background: var(--color-muted);
}

.chart-bar-lickd {
  background: var(--color-lickd-pink);
}

.chart-grid.in-view .chart-bar {
  animation: chart-bar-in 0.6s ease-out forwards;
}

.chart-axis {
  position: absolute;
  inset: 0 auto 0 0;
  width: var(--chart-gutter);
}

.chart-axis-tick {
  position: absolute;
  right: 0;
  padding-right: var(--spacing-xxs);
  font-size: 0.75rem;
  line-height: 1;
  color: var(--color-muted);
  white-space: nowrap;
}

.chart-axis-tick-max {
  top: 0;
  transform: translateY(-50%);
}

.chart-axis-tick-zero {
  bottom: 0;
  transform: translateY(50%);
}

.chart-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
  flex-wrap: wrap;
  gap: 8px;
}
.chart-title {
  margin: 0;
}
.legend {
  display: flex;
  gap: 14px;
}
.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
}
.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 2px;
}
.legend-dot-actual {
  background: var(--color-muted);
}
.legend-dot-lickd {
  background: var(--color-lickd-pink);
}

/* Padded to match .chart-plot so "Oldest"/"Newest" stay aligned with the first
   and last bars rather than the axis gutter. */
.chart-labels {
  display: flex;
  justify-content: space-between;
  gap: var(--spacing-default);
  padding-left: var(--chart-gutter);
}

.chart-label {
  font-size: 0.75rem;
}

.comps {
  margin-top: var(--spacing-2xl);
}
.comps-list {
  border-radius: var(--radius);
  overflow: hidden;
}
.comp-card {
  position: relative;
  padding: var(--spacing-default);
  display: grid;
  grid-template-columns: auto 1fr auto;
  grid-template-rows: auto;
  align-items: center;
  gap: var(--spacing-sm);
}

.comp-card:nth-of-type(even) {
  background-color: var(--color-bg-light);
}

.comp-card--final::after {
  content: '';
  display: block;
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, transparent, var(--color-bg) 90%);
}

.comp-icon {
  font-size: 1.25rem;
  align-self: start;
}
.comp-content {
}
.comp-name {
  font-weight: 700;
  font-size: 1.25rem;
  color: var(--color-lickd-green);
}
.comp-body {
  font-size: 0.75rem;
}
.comp-meta {
  font-size: 0.75rem;
  color: var(--color-muted);
  margin-top: var(--spacing-xs);
}

.comp-uplift {
  font-size: 1rem;
  font-weight: 700;
  color: var(--color-lickd-green);
}

.cta {
  margin-top: var(--spacing-2xl);
}

.cta-btn {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--spacing-xs);
  margin: 0 auto;
  font-size: 1.25rem;
  width: fit-content;
}

.cta-btn svg {
  transition: transform var(--transition-short) ease-in-out;
}

.cta-btn:hover svg {
  transform: translateX(4px);
}

.disclaimer {
  font-size: 0.75rem;
  color: var(--color-muted);
  margin-top: var(--spacing-xl);
  text-wrap: balance;
}

.recommended-plan-wrapper {
  margin-top: var(--spacing-2xl);
}

.recommended-plan-intro {
  text-wrap: balance;
  max-width: 500px;
  margin: 0 auto var(--spacing-2xl);
}

.currency-toggle {
  display: flex;
  justify-content: center;
  margin-top: var(--spacing-lg);
}

.currency-radio-wrapper {
  position: relative;
}

.currency-radio-label {
  display: block;
  cursor: pointer;
  padding: var(--spacing-xs) var(--spacing-sm);
  background-color: var(--color-bg-light);
  transition-property: color, background-color;
  transition: var(--transition-short) ease-in-out;
  font-weight: 700;
}

.currency-radio-wrapper:first-of-type .currency-radio-label {
  border-top-left-radius: var(--radius-sm);
  border-bottom-left-radius: var(--radius-sm);
}

.currency-radio-wrapper:last-of-type .currency-radio-label {
  border-top-right-radius: var(--radius-sm);
  border-bottom-right-radius: var(--radius-sm);
}

.currency-radio-wrapper input:checked + .currency-radio-label,
.currency-radio-label:hover {
  background-color: var(--color-lickd-pink);
  color: #070b1e;
}

.currency-radio-wrapper input:focus-visible + .currency-radio-label {
  outline: 1px solid var(--color-lickd-pink);
  outline-offset: 1px;
}

.plan-card {
  position: relative;
  border-radius: var(--radius-lg);
  padding: var(--spacing-lg);
  padding-top: var(--spacing-xl);
  background-color: var(--color-bg-light);
  width: min(360px, 100%);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-default);
}

.plan-card.in-view::before {
  content: '';
  position: absolute;
  display: block;
  inset: 0;
  border-radius: inherit;
  box-shadow: 0px 0px 32px 0px var(--color-lickd-pink-transparent);
  animation: pulse-glow 1s backwards;
  z-index: -1;
}

.plan-card .cta-btn {
  width: 100%;
  margin-top: var(--spacing-lg);
}

.plan-flag {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: var(--color-lickd-pink);
  color: #070b1e;
  padding: var(--spacing-xxs) var(--spacing-xs);
  border-radius: var(--radius-sm);
  font-size: 1rem;
  font-weight: 700;
  width: max-content;
  max-width: 100%;
}

.plan-heading {
  font-size: 2.5rem;
  margin: 0;
  color: var(--color-lickd-pink);
}

.plan-price {
  font-family: var(--display);
  line-height: 1;
  font-size: 1.75rem;
  white-space: pre-wrap;
}

.plan-price-large {
  font-size: 3rem;
}

.plan-price-month {
  font-family: var(--font);
  font-size: 1rem;
  font-weight: 700;
  color: var(--color-muted);
}

.plan-features {
  list-style-type: none;
  margin: 0;
  padding: 0;
}

.plan-features-item {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-xxs);
}

.plan-features-item > svg {
  flex-shrink: 0;
}

.plan-features-item:not(:first-of-type) {
  margin-top: var(--spacing-xs);
}

@media (max-width: 599px) {
  .channel-card {
    flex-direction: column;
    text-align: center;
  }
  .channel-badges {
    justify-content: center;
  }

  .chart-grid {
    height: 150px;
  }

  .comp-card {
    grid-template-columns: auto 1fr;
  }

  .comp-uplift {
    grid-row-start: 2;
    grid-column-start: 2;
  }
}

@media (min-width: 1024px) {
  .narrative-cards {
    grid-template-columns: repeat(3, 1fr);
  }
}
